import { useEffect, useRef, useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { useFixedUIHidden } from '~/modules/root/hook/use-fixed-ui-hidden';
import {
    FLOATING_DOCK_GAP_REM,
    FLOATING_FEEDBACK_HEIGHT_VAR,
    floatingFeedbackOffset,
} from '~/modules/root/util/floating-dock';
import { SiteFeedbackModal } from './SiteFeedbackModal';
import { useSiteFeedback } from '../hook/use-site-feedback';

/**
 * The floating feedback control, bottom right, for ArchiPro staff on the revamp beta.
 *
 * It takes its place in the shared bottom-right stack rather than picking a corner and hoping:
 * it sits directly above the chat dock and publishes its own height, so Back to top rides above
 * it. It also answers `useFixedUIHidden`, which is how the image viewer and the tagging mode
 * clear every floating control off the page at once.
 *
 * Mount once, at the app shell. Gating lives at the mount site, not here.
 */
export const SiteFeedbackButton = () => {
    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const isFixedUIHidden = useFixedUIHidden();
    const { submit, submitting } = useSiteFeedback();

    // Publish the space taken in the corner, gap included, so Back to top clears it. Removed
    // while the page hides its fixed chrome, since the corner is empty then.
    useEffect(() => {
        const { style } = document.documentElement;
        const button = buttonRef.current;
        if (isFixedUIHidden || !button) {
            style.removeProperty(FLOATING_FEEDBACK_HEIGHT_VAR);
            return;
        }
        style.setProperty(
            FLOATING_FEEDBACK_HEIGHT_VAR,
            `calc(${button.getBoundingClientRect().height}px + ${FLOATING_DOCK_GAP_REM}rem)`,
        );
        return () => {
            style.removeProperty(FLOATING_FEEDBACK_HEIGHT_VAR);
        };
    }, [isFixedUIHidden]);

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setOpen(true)}
                aria-label="Give feedback on this page"
                style={{ bottom: floatingFeedbackOffset() }}
                className={cn(
                    'fixed right-4 z-fixed flex h-11 items-center gap-2 rounded-[1rem] px-4 md:right-8',
                    // Near-black fill matches light pages; a light ring keeps an
                    // edge on dark grounds (project revamp #181818) where the fill
                    // would otherwise disappear into the page.
                    'bg-[#181818] text-white shadow-lg ring-1 ring-white/35 transition-colors hover:bg-[#343434]',
                    'focus-ring-on-dark',
                    isFixedUIHidden && 'hidden',
                )}
            >
                {/* Speech bubble: the control is about saying something, and the mark reads at
                    this size where a pencil or a flag do not. */}
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    className="size-[1.1rem]"
                >
                    <path
                        d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9.9 9.9 0 0 1-3.2-.5L3 21l1.6-4.3A8.2 8.2 0 0 1 3.6 11.5a8.4 8.4 0 0 1 9-8.4 8.4 8.4 0 0 1 8.4 8.4Z"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
                <span className="text-3.5 font-medium tracking-tight max-md:sr-only">
                    Feedback
                </span>
            </button>

            <SiteFeedbackModal
                open={open}
                onOpenChange={setOpen}
                onSubmit={submit}
                submitting={submitting}
            />
        </>
    );
};
